import { ORDER_UNITS, type OrderItemInput } from '@faktorplus/shared';
import { Button } from '../../components/ui/button';
import { Input, Select } from '../../components/ui/form';
import styles from './order-page.module.css';

export interface DraftOrderItem {
  name: string;
  quantity: string;
  unit: string;
}

export const EMPTY_ORDER_ITEM: DraftOrderItem = { name: '', quantity: '1', unit: 'szt' };

/**
 * What is being ordered.
 *
 * Deliberately three fields and no price: the manager on site knows what they
 * need and how much of it, and the merchant prices it. Asking for a price here
 * would only produce numbers nobody checks against the invoice that follows.
 */
export function OrderItemsEditor({
  items,
  onChange,
}: {
  items: DraftOrderItem[];
  onChange: (items: DraftOrderItem[]) => void;
}) {
  function update(index: number, patch: Partial<DraftOrderItem>) {
    onChange(items.map((item, position) => (position === index ? { ...item, ...patch } : item)));
  }

  function remove(index: number) {
    onChange(items.filter((_, position) => position !== index));
  }

  return (
    <div className={styles.editor}>
      <ul className={styles.rows}>
        {items.map((item, index) => (
          <li key={index} className={styles.itemRow}>
            <div className={styles.rowHead}>
              <span className={styles.rowIndex}>{index + 1}.</span>
              <Input
                aria-label={`Nazwa pozycji ${index + 1}`}
                placeholder="np. Beton B25, pustak Porotherm 25"
                value={item.name}
                onChange={(event) => update(index, { name: event.target.value })}
              />
              <button
                type="button"
                className={styles.remove}
                // The last row is the order; removing it leaves nothing to send.
                disabled={items.length === 1}
                onClick={() => remove(index)}
                aria-label={`Usuń pozycję ${index + 1}`}
              >
                ✕
              </button>
            </div>

            <div className={styles.rowFields}>
              <Input
                type="number"
                min="0"
                step="0.001"
                aria-label={`Ilość pozycji ${index + 1}`}
                placeholder="Ilość"
                value={item.quantity}
                onChange={(event) => update(index, { quantity: event.target.value })}
              />

              <Select
                aria-label={`Jednostka pozycji ${index + 1}`}
                value={item.unit}
                onChange={(event) => update(index, { unit: event.target.value })}
              >
                {ORDER_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
            </div>
          </li>
        ))}
      </ul>

      <Button onClick={() => onChange([...items, { ...EMPTY_ORDER_ITEM }])}>
        + Dodaj pozycję
      </Button>
    </div>
  );
}

export function toOrderItems(items: DraftOrderItem[]): OrderItemInput[] {
  return items.map((item) => ({
    name: item.name.trim(),
    quantity: Number(item.quantity) || 0,
    unit: item.unit,
  }));
}
